/**
 * 첨부 다운로드 공통 경로 — 화면 세 곳(메일 상세 / 첨부 라이브러리 / 새 창)이 같은 규칙을 쓰도록.
 *
 * <h3>브라우저 네이티브 다운로드를 쓴다 (XHR + blob 아님)</h3>
 * 다운로드 링크를 새 탭으로 열면 그 뒤는 브라우저가 다 한다 — 저장 위치·진행률·재개·기록.
 * 우리가 할 일은 <b>인증이 실린 URL 을 만드는 것</b>뿐이다.
 *
 * <h3>왜 XHR + blob 을 걷어냈나 — 실제로 깨졌다</h3>
 * 예전 구현은 XHR 로 파일 전체를 받아 blob 으로 만든 뒤 임시 {@code <a download>} 를 눌렀다.
 * 그런데 그 click 은 {@code await} <b>뒤</b>에 실행돼서 사용자 제스처가 이미 만료된 상태다.
 * 브라우저는 이걸 "사이트가 스스로 시작한 자동 다운로드" 로 분류하고 탭 단위로 관리하는데,
 * 그 상태에서 저장 확인창을 한 번 <b>취소</b>하면 <b>그 탭은 새로고침 전까지 이후 다운로드를
 * 조용히 폐기</b>한다. 서버 감사로그에는 요청이 정상으로 남고 파일도 다 내려오는데 화면에서는
 * 아무 일도 안 일어난다 — 원인을 짐작할 단서가 하나도 없는 종류의 고장이었다.
 * (실측 2026-08-17: 같은 첨부를 15~17초 간격으로 3번 눌러도 저장창이 안 뜸. 새로고침하면 복구.)
 *
 * 두 번째 고장도 같은 자리에 있었다 — blob URL 을 10초 뒤 해제했는데, 저장 확인창은 사용자가
 * 저장을 누르기 전까지 blob 을 읽지 않는다. 창을 10초 넘게 띄워 두면 주소가 먼저 죽어서
 * 저장을 눌러도 조용히 실패했다. 사람이 창을 열어 두는 시간에는 상한이 없으니 타이머로는
 * 못 막는 문제였다.
 *
 * <h3>덤으로 사라진 것들</h3>
 * <ul>
 *   <li>진행률 콜백 / 중복 클릭 차단 — 브라우저 기본 다운로드 UI 가 대신한다</li>
 *   <li>9.7MB 짜리 첨부가 JS 힙에 통째로 올라가던 것</li>
 *   <li>blob URL 수명 관리 전부</li>
 * </ul>
 *
 * <h3>인증 — 왜 ?token= 인가</h3>
 * 다운로드 endpoint 는 X-TOKEN 헤더 인증인데 브라우저가 스스로 여는 요청(새 탭·네이티브 다운로드)은
 * 커스텀 헤더를 못 싣는다. {@code EmployeeTokenInterceptor} 가 헤더가 없으면 {@code ?token=} 쿼리를
 * 읽도록 <b>전 경로에 대해</b> 이미 열려 있어(경로 제한 없음) 그대로 쓴다.
 * 인라인 이미지·서명 이미지도 같은 이유로 이미 이 방식이다(MailBodyHtml.rewriteImageUrls).
 *
 * <p><b>알려진 대가</b> — 세션 토큰이 URL 에 실리므로 nginx 액세스 로그와 브라우저 <b>다운로드 기록</b>에
 * 남는다. 인라인 이미지 쪽은 이미 같은 상태지만 다운로드 기록은 사용자 눈에 보이는 자리라 노출 면적이
 * 더 넓다. 없애려면 인라인·첨부를 묶어 <b>단명 서명 티켓</b>(첨부 1건 전용·수 분 만료)으로 옮겨야 한다 —
 * 이 파일만 고쳐서는 반쪽이라 별도 과제로 둔다.
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * 첨부 다운로드 URL — {@code <a href>} 에 그대로 물리면 된다.
 *
 * <p>토큰이 없으면(로그아웃 상태) {@code null}. 호출부는 null 이면 버튼을 비활성화한다 —
 * 링크를 열어봐야 401 JSON 이 새 탭에 뜰 뿐이라 그게 더 나쁘다.
 *
 * <p>서버가 {@code Content-Disposition: attachment} 를 붙이므로 새 탭은 즉시 다운로드로 바뀌고
 * 빈 탭은 브라우저가 알아서 닫는다. 파일명(한글 NFC·RFC 5987)도 서버 헤더가 정한다.
 *
 * @param {number} attachmentId
 * @returns {string|null}
 */
export function attachmentDownloadUrl(attachmentId) {
    const token = localStorage.getItem("auth_token");
    if (!token) return null;
    return `${API_BASE}/mail/attachments/${attachmentId}?token=${encodeURIComponent(token)}`;
}

/**
 * 링크로 못 만드는 자리(메뉴 항목 등)를 위한 명령형 버전.
 *
 * <p><b>반드시 클릭 핸들러에서 동기적으로</b> 불러야 한다. {@code await} 뒤에 부르면 사용자 제스처가
 * 끊겨 팝업 차단에 걸리고, 이 파일이 고치려는 그 문제로 되돌아간다. 가능하면 {@code <a href>} 를 쓴다.
 *
 * @returns {boolean} 열었으면 true, 토큰이 없어 못 열었으면 false
 */
export function openAttachmentDownload(attachmentId) {
    const url = attachmentDownloadUrl(attachmentId);
    if (!url) return false;
    window.open(url, "_blank", "noopener");
    return true;
}
